import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { PriceQuote } from '@pipyar/core';
import { useSymbol } from '@/lib/useSymbol';
import { quoteKey } from '@/features/alerts/api';

/**
 * Keeps the selected pair's price live, for the whole app.
 *
 * One EventSource, opened in the app shell, writing every pushed quote into
 * the same query cache the rest of the app already reads with `useQuote`. So
 * nothing downstream knows or cares whether a price arrived by push or poll:
 * the charts' last candle, the feed badge and banner, and the price-alert
 * checks all just see the cache change, several times a second.
 *
 * EventSource reconnects by itself after a drop (the server asks for 2s), and
 * the quote poll keeps working underneath as a fallback while it does.
 */
export function LiveStream() {
  const { symbol } = useSymbol();
  const qc = useQueryClient();

  useEffect(() => {
    if (!symbol || typeof EventSource === 'undefined') return;
    const source = new EventSource(`/api/live/stream?symbol=${encodeURIComponent(symbol)}`);
    source.onmessage = (e: MessageEvent<string>) => {
      try {
        const quote = JSON.parse(e.data) as PriceQuote;
        if (quote.symbol === symbol) qc.setQueryData(quoteKey(symbol), quote);
      } catch {
        /* a malformed message is skipped; the next one replaces it */
      }
    };
    return () => source.close();
  }, [symbol, qc]);

  return null;
}
